import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

const CARDS: { to: string; title: string; desc: string }[] = [
  { to: '/comptabilite/journal', title: 'Journal', desc: 'Écritures comptables par journal et période' },
  { to: '/comptabilite/grand-livre', title: 'Grand-livre', desc: 'Mouvements détaillés par compte' },
  { to: '/comptabilite/balance', title: 'Balance générale', desc: 'Soldes débiteurs / créditeurs par compte' },
  { to: '/comptabilite/bilan', title: 'Bilan', desc: 'Actif / Capitaux propres & passifs (SCE)' },
  { to: '/comptabilite/etat-resultat', title: 'État de résultat', desc: 'Produits − charges → résultat net' },
  { to: '/comptabilite/plan-comptable', title: 'Plan comptable', desc: 'Comptes SCE (classes 1 à 7), personnalisable' },
  { to: '/comptabilite/journaux', title: 'Journaux', desc: 'VE, AC, BQ, CA, OD, AN' },
  { to: '/comptabilite/charges', title: 'Charges & dépenses', desc: 'Loyer, notes de frais, honoraires… → écriture' },
  { to: '/comptabilite/analytique', title: 'Comptabilité analytique', desc: 'Axes projet/dépôt/activité, résultat par axe' },
  { to: '/comptabilite/exercices', title: 'Exercices & clôture', desc: 'Verrouillage de période, résultat, à-nouveaux' },
  { to: '/comptabilite/lettrage', title: 'Lettrage & balance âgée', desc: 'Rapprocher tiers, créances/dettes par ancienneté' },
  { to: '/comptabilite/immobilisations', title: 'Immobilisations', desc: 'Registre des actifs & amortissements (28x)' },
  { to: '/comptabilite/rapprochement', title: 'Rapprochement bancaire', desc: 'Pointage des mouvements vs relevé' },
  { to: '/comptabilite/parametres', title: 'Paramètres comptables', desc: 'Mappage des comptes (TVA, ventes, achats…)' },
  { to: '/comptabilite/expert', title: 'Espace expert-comptable', desc: 'Lecture commentée des écritures, invitation de l\'expert' },
];

const Comptabilite: React.FC = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState<string>('');
  const [nbEcritures, setNbEcritures] = useState<number | null>(null);
  const [totalMouvements, setTotalMouvements] = useState<number>(0);

  const refresh = async () => {
    try {
      const [v, ecritures] = await Promise.all([
        comptabiliteService.planVersion(),
        comptabiliteService.listEcritures(startDate, endDate),
      ]);
      setVersion(v.version);
      setNbEcritures(ecritures.length);
      setTotalMouvements(ecritures.reduce((s, e) => s + Number(e.totalDebit || 0), 0));
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Impossible de charger le module comptable.');
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const generer = async () => {
    setBusy(true);
    try {
      const res = await comptabiliteService.generer(startDate, endDate);
      toast.success(
        `Écritures générées : ${res.documentsComptabilises} documents, ${res.paiementsComptabilises} règlements.`,
      );
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la génération.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comptabilité générale</h1>
          <p className="text-sm text-gray-500">
            Comptabilité en partie double conforme au Système Comptable des Entreprises (NCT).
            {version && <span className="ml-2 text-xs text-gray-400">Plan {version}</span>}
          </p>
        </div>
      </div>

      {/* Génération automatique */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Générer les écritures de la période</h2>
          <p className="text-sm text-gray-500">
            Crée automatiquement les écritures à partir des factures, avoirs et règlements
            (ventes &amp; achats). Les écritures déjà validées sont préservées.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Du</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                   className="rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Au</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                   className="rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <button onClick={generer} disabled={busy}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Génération…' : 'Générer les écritures'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="text-sm text-gray-500">Écritures sur la période</div>
          <div className="text-2xl font-bold text-gray-900">{nbEcritures ?? '—'}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="text-sm text-gray-500">Total mouvementé (débit)</div>
          <div className="text-2xl font-bold text-gray-900">{formatTND(totalMouvements)}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="text-sm text-gray-500">Référentiel</div>
          <div className="text-2xl font-bold text-gray-900">{version || '—'}</div>
        </div>
      </div>

      {/* Navigation modules */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <Link key={c.to} to={c.to}
                className="block bg-white p-5 rounded-xl border border-gray-200 hover:border-blue-400 hover:shadow-sm transition">
            <div className="font-semibold text-gray-900">{c.title}</div>
            <div className="text-sm text-gray-500 mt-1">{c.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Comptabilite;
